import dayjs from 'dayjs';
import { pointerMove } from 'ol/events/condition';
import Feature from 'ol/Feature';
import { LineString } from 'ol/geom';
import { Select } from 'ol/interaction';
import { Vector as VectorLayer } from 'ol/layer';
import { fromLonLat } from 'ol/proj';
import { Vector as VectorSource } from 'ol/source';

import { STYLES } from '@/define/olMaps';

/**
 * Trace(이동 경로) 관리 Feature
 */
class TraceFeature {
  constructor(map) {
    this.map = map;
    this.traces = {};
  }

  /**
   * 이동 경로 추가
   * @param {string} robotId - 로봇 ID
   * @param {Object} position - { lat, lng } 좌표
   * @param {string} traceType - 'full' 또는 'recent'
   */
  add(robotId, { lat, lng }, traceType) {
    const position = fromLonLat([lng, lat]);
    const now = dayjs();

    // 기존 이동 경로
    if (this.traces[robotId]) {
      const { layer, trace } = this.traces[robotId];

      trace.full.push(position);
      trace.recent.push({ ...position, time: now.valueOf() });

      // 10분 전
      const tenMinutesAgo = now.subtract(10, 'minutes');
      // 최근 10분 이내 시작 지점
      const startIndex = trace.recent.findIndex(({ time }) => dayjs(time).isAfter(tenMinutesAgo));
      // 10분 초과 지점 제거
      trace.recent.splice(0, startIndex);

      const feature = layer.getSource().getFeatures()[0];
      // 전체 경로 표시인 경우
      if (traceType === 'full') {
        feature.getGeometry().appendCoordinate(position);
      }
      // 최근 경로 표시인 경우
      else {
        const coordinates = trace.recent.map(({ 0: lng, 1: lat }) => [lng, lat]);
        feature.getGeometry().setCoordinates(coordinates);
      }
    }
    // 신규 이동 경로
    else {
      const feature = new Feature(new LineString([position]));
      const source = new VectorSource({ features: [feature] });
      const layer = new VectorLayer({
        source,
        style: STYLES.LINE.TRACE,
      });

      this.map.addLayer(layer);
      this.traces[robotId] = {
        layer,
        trace: {
          full: [position],
          recent: [{ ...position, time: now.valueOf() }],
        },
      };

      // Mouse over 시 강조
      const handleHover = new Select({
        condition: pointerMove,
        layers: [layer],
      });
      this.map.addInteraction(handleHover);
    }
  }

  /**
   * 이동 경로 제거
   * @param {string} robotId - 로봇 ID
   */
  remove(robotId) {
    if (!this.traces[robotId]) return;

    const { layer, trace } = this.traces[robotId];

    const feature = layer.getSource().getFeatures()[0];
    feature.getGeometry().setCoordinates([]);

    trace.full = [];
    trace.recent = [];
  }

  /**
   * 경로 타입 변경 (전체/최근)
   * @param {string} robotId - 로봇 ID
   * @param {string} traceType - 'full' 또는 'recent'
   */
  setType(robotId, traceType) {
    if (!this.traces[robotId]) return;

    const { layer, trace } = this.traces[robotId];
    const feature = layer.getSource().getFeatures()[0];

    // 전체 경로 표시인 경우
    if (traceType === 'full') {
      feature.getGeometry().setCoordinates(trace.full);
    }
    // 최근 경로 표시인 경우
    else {
      const coordinates = trace.recent.map(({ 0: lng, 1: lat }) => [lng, lat]);
      feature.getGeometry().setCoordinates(coordinates);
    }
  }

  /**
   * 경로 표시/숨김
   * @param {string} robotId - 로봇 ID
   * @param {boolean} visible - 표시 여부
   */
  setVisible(robotId, visible) {
    if (!this.traces[robotId]) return;

    this.traces[robotId].layer.setVisible(visible);
  }

  /**
   * Trace 데이터 가져오기
   * @param {string} robotId - 로봇 ID
   * @returns {Object|undefined} Trace 데이터
   */
  get(robotId) {
    return this.traces[robotId];
  }
}

export default TraceFeature;
